/**
 * ย่อรูปก่อนอัปโหลด
 *
 * เหตุผลสำคัญ: รูป 12MP จากมือถือมีขนาดราว 4MB การอัปโหลดในพื้นที่สัญญาณอ่อน
 * อาจใช้เวลาเป็นนาทีและล้มเหลวบ่อย ย่อเหลือด้านยาว 1600px + JPEG q0.8
 * ได้ราว 250KB ซึ่งยังชัดพอสำหรับดูสภาพบ้าน และลดค่าเก็บ R2 ลงมาก
 */

const MAX_EDGE = 1600;
const QUALITY = 0.8;

export interface ProcessedImage {
  blob: Blob;
  width: number;
  height: number;
  sizeBytes: number;
  /** ใช้แสดงตัวอย่างในหน้าจอ — ต้อง revoke เมื่อเลิกใช้ */
  previewUrl: string;
}

export async function shrinkImage(file: File | Blob): Promise<ProcessedImage> {
  const bitmap = await createImageBitmap(file);

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('เบราว์เซอร์ไม่รองรับการย่อรูป');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', QUALITY),
  );
  if (!blob) throw new Error('ย่อรูปไม่สำเร็จ');

  return {
    blob,
    width,
    height,
    sizeBytes: blob.size,
    previewUrl: URL.createObjectURL(blob),
  };
}

/**
 * แฮชไฟล์เพื่อกันอัปโหลดรูปเดิมซ้ำ และใช้ตรวจความถูกต้องภายหลัง
 * (การ strip EXIF เกิดขึ้นอัตโนมัติจากการวาดลง canvas — metadata ไม่ติดไปกับไฟล์ใหม่
 *  ส่วนพิกัดที่ต้องใช้เป็นหลักฐานถูกส่งแยกเป็นฟิลด์ ดู docs/05-security-pdpa.md)
 */
export async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export interface Coords {
  lat: number;
  lng: number;
  accuracy: number;
}

/** ขอพิกัดปัจจุบัน — คืน null เมื่อผู้ใช้ปฏิเสธหรือหาไม่เจอ (ไม่ throw) */
export function getCurrentPosition(timeoutMs = 12_000): Promise<Coords | null> {
  if (!('geolocation' in navigator)) return Promise.resolve(null);

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 },
    );
  });
}
